import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Admin Interface Entry Point
 *
 * WordPress AI Chatbot Pro - Admin Dashboard
 *
 * @package WooAIChatbot
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { AdminLayout } from './components/AdminLayout';
import { Dashboard } from './pages/Dashboard';
import { AIProviders } from './pages/AIProviders';
import { RAG } from './pages/RAG';
// PLAYBOOKS DISABLED import { Playbooks } from './pages/Playbooks';
import { Topics } from './pages/Topics';
import { Analytics } from './pages/Analytics';
import { AnalyticsSettings } from './pages/AnalyticsSettings';
import { Appearance } from './pages/Appearance';
// LANGUAGES DISABLED import { Languages } from './pages/Languages';
import { PromotionsContainer } from './pages/PromotionsContainer';
import { Logs } from './pages/Logs';
import { License } from './pages/License';
import { SettingsProvider } from '../contexts/SettingsContext';
import { AnalyticsProvider, useAnalytics } from '../contexts/AnalyticsContext';
import './styles.css';
const pageSlugMap = {
    'dashboard': 'woo-ai-chatbot',
    'ai-providers': 'woo-ai-chatbot-providers',
    'rag': 'woo-ai-chatbot-rag',
    // PLAYBOOKS DISABLED 'playbooks': 'woo-ai-chatbot-playbooks',
    'topics': 'woo-ai-chatbot-topics',
    'promotions': 'woo-ai-chatbot-promotions',
    'analytics': 'woo-ai-chatbot-analytics',
    'analytics-settings': 'woo-ai-chatbot-analytics-settings',
    'appearance': 'woo-ai-chatbot-appearance',
    // LANGUAGES DISABLED 'languages': 'woo-ai-chatbot-languages',
    'logs': 'woo-ai-chatbot-logs',
    'license': 'woo-ai-chatbot-settings',
};
/**
 * Resolve AdminPage from URL query param or localized config
 */
const getPageFromUrl = () => {
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const slug = params.get('page');
        if (slug) {
            const match = Object.entries(pageSlugMap).find(([, s]) => s === slug);
            if (match)
                return match[0];
        }
    }
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    const currentSlug = globals?.currentPage;
    if (currentSlug) {
        const match = Object.entries(pageSlugMap).find(([, slug]) => slug === currentSlug);
        if (match)
            return match[0];
    }
    return 'dashboard';
};
const ANALYTICS_PAGES = ['dashboard', 'analytics', 'analytics-settings'];
/**
 * Update WordPress admin left-sidebar active menu item to match SPA navigation.
 */
const updateWpAdminMenu = (slug) => {
    const menuLinks = document.querySelectorAll('#adminmenu a');
    menuLinks.forEach((link) => {
        const href = link.getAttribute('href') || '';
        const li = link.closest('li');
        if (!li)
            return;
        if (href.includes(`page=${slug}`)) {
            li.classList.add('current');
            // Also activate parent menu if it's a submenu item
            const parentLi = li.closest('li.wp-has-submenu');
            if (parentLi) {
                parentLi.classList.add('wp-has-current-submenu', 'wp-menu-open');
                parentLi.classList.remove('wp-not-current-submenu');
            }
        }
        else if (href.includes('page=woo-ai-chatbot')) {
            li.classList.remove('current');
        }
    });
};
/**
 * Admin App Component — SPA navigation via pushState
 */
const AdminApp = () => {
    const [currentPage, setCurrentPage] = useState(() => getPageFromUrl());
    const { refresh: refreshAnalytics } = useAnalytics();
    // Handle browser back/forward buttons
    useEffect(() => {
        const onPopState = () => {
            const page = getPageFromUrl();
            setCurrentPage(page);
            if (ANALYTICS_PAGES.includes(page)) {
                refreshAnalytics();
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [refreshAnalytics]);
    const handleNavigate = useCallback((page) => {
        const slug = pageSlugMap[page];
        if (!slug)
            return;
        // Update URL without full page reload
        const url = new URL(window.location.href);
        url.searchParams.set('page', slug);
        window.history.pushState({ page }, '', url.toString());
        // Update WP admin menu active state
        updateWpAdminMenu(slug);
        setCurrentPage(page);
        // Lazy-load analytics when navigating to analytics pages
        if (ANALYTICS_PAGES.includes(page)) {
            refreshAnalytics();
        }
    }, [refreshAnalytics]);
    const renderPage = () => {
        switch (currentPage) {
            case 'dashboard':
                return _jsx(Dashboard, {});
            case 'ai-providers':
                return _jsx(AIProviders, {});
            case 'rag':
                return _jsx(RAG, {});
            /* PLAYBOOKS DISABLED
            case 'playbooks':
              return <Playbooks />;
            */
            case 'topics':
                return _jsx(Topics, {});
            case 'analytics':
                return _jsx(Analytics, {});
            case 'analytics-settings':
                return _jsx(AnalyticsSettings, {});
            case 'appearance':
                return _jsx(Appearance, {});
            /* LANGUAGES DISABLED
            case 'languages':
              return <Languages />;
            */
            case 'promotions':
                return _jsx(PromotionsContainer, {});
            case 'logs':
                return _jsx(Logs, {});
            case 'license':
                return _jsx(License, {});
            default:
                return _jsx(Dashboard, {});
        }
    };
    return (_jsx(AdminLayout, { currentPage: currentPage, onNavigate: handleNavigate, children: renderPage() }));
};
/**
 * Initialize Admin Interface
 */
const initAdmin = () => {
    const container = document.getElementById('woo-ai-chatbot-admin-root');
    if (!container) {
        console.error('Admin root element not found');
        return;
    }
    // Only auto-load analytics on pages that need it (dashboard, analytics)
    const initialPage = getPageFromUrl();
    const shouldAutoLoadAnalytics = ANALYTICS_PAGES.includes(initialPage);
    const root = createRoot(container);
    root.render(_jsx(React.StrictMode, { children: _jsx(SettingsProvider, { children: _jsx(AnalyticsProvider, { autoLoad: shouldAutoLoadAnalytics, children: _jsx(AdminApp, {}) }) }) }));
};
// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAdmin);
}
else {
    initAdmin();
}
export default AdminApp;
